import http from "../utils/http";

/**
 * Member-submitted diet-log plates for a single member.
 * Backend: `/admin/diet-logs` — admins only list / review / delete;
 * plate creation is member-only. `userId` is stripped server-side, so it
 * never appears on the wire. Timestamps are ISO-8601 UTC.
 *
 * List responses are the standard Spring `Page<T>` (unwrapped from the
 * `BizResponse` envelope at `.data.data`); the review endpoint returns the
 * updated `DietLog` directly under `.data.data`.
 */
export type DietCategory = "BREAKFAST" | "LUNCH" | "SNACKS" | "DINNER";

export interface DietLog {
  id: string;
  photoUrl: string;
  category: DietCategory;
  note: string | null;
  loggedAt: string;
  createdAt: string;
  /** Coach verdict — `null` = not yet reviewed, tri-state with true/false. */
  followed: boolean | null;
  coachComment: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
}

/** Coach fields sent on review. `followed: null` clears the verdict. */
export interface DietLogReviewRequest {
  followed: boolean | null;
  comment?: string | null;
}

/** Spring `Page<T>` shape returned by the list endpoint. */
export interface Page<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
}

interface ListParams {
  userId: string;
  page?: number;
  size?: number;
  /** `yyyy-MM-dd`, inclusive, resolved to IST start-of-day by the server. */
  startDate?: string;
  /** `yyyy-MM-dd`, inclusive, resolved to IST end-of-day by the server. */
  endDate?: string;
}

const listDietLogs = async ({
  userId,
  page = 0,
  size = 20,
  startDate,
  endDate,
}: ListParams): Promise<Page<DietLog>> => {
  // The endpoint defaults to `loggedAt` DESC (newest first) globally, before
  // pagination — so page 0 is the newest meals and paging forward walks
  // backward in time; no explicit sort param needed.
  let urlPath = `/admin/diet-logs/${userId}?page=${page}&size=${size}`;
  if (startDate) urlPath += `&startDate=${startDate}`;
  if (endDate) urlPath += `&endDate=${endDate}`;

  const result = await http.get({ path: urlPath, skipAuthRefresh: true });
  // The list endpoint returns a bare Spring `Page`, but tolerate the
  // `BizResponse` envelope too — neither a Page nor a DietLog has a `.data`
  // field, so this picks the right level for both shapes.
  const pageData = (result.data?.data ?? result.data) as Page<DietLog>;

  // The endpoint documents `loggedAt` DESC, but the backend list order isn't
  // guaranteed — sort the page defensively so plates always read newest first.
  const content = [...(pageData?.content ?? [])].sort(
    (a, b) => new Date(b.loggedAt).getTime() - new Date(a.loggedAt).getTime()
  );

  return { ...pageData, content };
};

const reviewDietLog = async (
  logId: string,
  review: DietLogReviewRequest
): Promise<DietLog> => {
  const result = await http.patch({
    path: `/admin/diet-logs/${logId}`,
    body: review,
  });
  return (result.data?.data ?? result.data) as DietLog;
};

const deleteDietLog = async (logId: string): Promise<void> => {
  await http.deleteHttp({ path: `/admin/diet-logs/${logId}`, body: {} });
};

export { listDietLogs, reviewDietLog, deleteDietLog };
